// Browse and edit Academy content.
//
// Until this page existed, every one of the lessons was hand-written JSON
// seeded from a developer machine — nobody else could see what the
// Academy contained, let alone change it.
//
// Editing goes through the same rules the seed pipeline runs, server
// side. That is the whole design constraint: a dashboard that could write
// content the seed would have rejected is worse than no dashboard, because
// the failure surfaces as a user losing a life to a question with no
// reachable right answer.
import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Archive,
  BookOpen,
  Check,
  Eye,
  EyeOff,
  GraduationCap,
  HelpCircle,
  Layers,
  LayoutGrid,
  List,
  Loader2,
  GripVertical,
  Pencil,
  Play,
  Plus,
  Search,
  Trash2,
  Sparkles,
  X,
} from "lucide-react";
import Layout from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAcademyAreas,
  useAcademyConcepts,
  useAcademyConcept,
  useSetConceptStatus,
  useUpdateAssetPayload,
  useAcademyAssetKinds,
  useAcademyVocabulary,
  useCreateAsset,
  useDeleteAsset,
  useReorderAssets,
  useMoveConcept,
  useSetPrerequisites,
} from "@/hooks/api/useAcademy";
import type {
  AcademyAdminAsset,
  AcademyAdminConcept,
  ContentStatus,
} from "@/api/services/academy.service";
import { toast } from "react-toastify";
import NewLessonDialog from "./NewLessonDialog";
import LessonPreview from "./preview/LessonPreview";
import PayloadForm, { FORM_KINDS } from "./preview/PayloadForm";
import PayloadSummary from "./preview/PayloadSummary";
import AssetPreview from "./preview/AssetPreview";
import { PITCH_COLOR } from "./preview/geometry";

const ALL = "__all__";

const STATUS_STYLE: Record<ContentStatus, string> = {
  PUBLISHED: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  DRAFT: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  ARCHIVED: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};

// One accent per area, keyed by position rather than by key, so a new
// area picks up a colour without anyone having to add it here.
const AREA_ACCENTS = [
  "from-emerald-500 to-teal-600",
  "from-sky-500 to-indigo-600",
  "from-violet-500 to-fuchsia-600",
  "from-amber-500 to-orange-600",
  "from-rose-500 to-pink-600",
];

function StatusPill({ status }: { status: ContentStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${STATUS_STYLE[status]}`}
    >
      {status}
    </span>
  );
}

export default function AcademyContentPage() {
  const [areaKey, setAreaKey] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Table by default: the list is already two dozen rows and only
  // grows, and scanning "which of these is still a draft" down a column
  // beats reading it off a grid of cards. Remembered per browser,
  // because this is a working preference and re-choosing it on every
  // visit is a papercut.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [view, setView] = useState<"table" | "cards">(() => {
    try {
      return localStorage.getItem("academy-content-view") === "cards"
        ? "cards"
        : "table";
    } catch {
      return "table";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("academy-content-view", view);
    } catch {
      /* private mode — the choice just does not persist */
    }
  }, [view]);

  const { data: areas } = useAcademyAreas();
  const { data: concepts, isLoading } = useAcademyConcepts({
    areaKey: areaKey === ALL ? undefined : areaKey,
    status: status === ALL ? undefined : (status as ContentStatus),
  });

  // Searching client-side: the whole list is a couple of dozen rows and
  // already in memory, so a round trip per keystroke would be slower and
  // would need an endpoint that does not exist yet.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return concepts ?? [];
    return (concepts ?? []).filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        c.stageName.toLowerCase().includes(q)
    );
  }, [concepts, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));

  // Filtering down to fewer results than the page you were on would
  // otherwise leave an empty list with no obvious way back — the rows
  // are there, you are just standing past the end of them.
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => visible.slice((page - 1) * pageSize, page * pageSize),
    [visible, page, pageSize]
  );

  const totals = useMemo(() => {
    const list = concepts ?? [];
    return {
      lessons: list.length,
      questions: list.reduce((s, c) => s + c.questionCount, 0),
      live: list.filter((c) => c.status === "PUBLISHED").length,
    };
  }, [concepts]);

  // Any change to what is being looked at starts again at page one.
  useEffect(() => {
    setPage(1);
  }, [areaKey, status, search]);

  const hasFilters = areaKey !== ALL || status !== ALL || !!search;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header. The three numbers are the questions an author actually
            arrives with: how much is there, how much is live, how much of
            it is a question rather than prose. */}
        <div className="rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-6 text-white shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-white/15 p-2.5 backdrop-blur">
                <GraduationCap className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Academy Content
                </h1>
                <p className="text-sm text-white/70">
                  Every lesson, its questions and its answer keys.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {[
                { label: "Lessons", value: totals.lessons },
                { label: "Live", value: totals.live },
                { label: "Questions", value: totals.questions },
              ].map((s) => (
                <div key={s.label} className="text-right">
                  <div className="text-2xl font-bold tabular-nums">
                    {s.value}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-white/60">
                    {s.label}
                  </div>
                </div>
              ))}
              <Button
                className="cursor-pointer gap-1.5 bg-white text-emerald-700 hover:bg-white/90"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-4 w-4" />
                New lesson
              </Button>
            </div>
          </div>
        </div>

        {/* Area cards */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {(areas ?? []).map((area, i) => {
            const total = area.stages.reduce((s, st) => s + st.conceptCount, 0);
            const live = area.stages.reduce(
              (s, st) => s + st.publishedConceptCount,
              0
            );
            const pct = total ? Math.round((live / total) * 100) : 0;
            const selected = areaKey === area.key;
            return (
              <button
                key={area.key}
                type="button"
                onClick={() => setAreaKey(selected ? ALL : area.key)}
                className={`group cursor-pointer overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  selected
                    ? "border-emerald-500 ring-2 ring-emerald-500/20"
                    : "border-border"
                }`}
              >
                <div
                  className={`h-1.5 w-full bg-gradient-to-r ${
                    AREA_ACCENTS[i % AREA_ACCENTS.length]
                  }`}
                />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{area.name}</span>
                    </div>
                    {selected && (
                      <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                    )}
                  </div>
                  <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                    {area.sub}
                  </p>

                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="text-xl font-bold tabular-nums">
                      {live}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      / {total} live
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {area.stages.length} stage
                      {area.stages.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {/* The bar says at a glance which areas still have
                      drafts sitting in them. */}
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${
                        AREA_ACCENTS[i % AREA_ACCENTS.length]
                      } transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3 shadow-sm">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lessons…"
              className="pl-9"
            />
          </div>

          <Select value={areaKey} onValueChange={setAreaKey}>
            <SelectTrigger className="w-48 cursor-pointer">
              <SelectValue placeholder="All areas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All areas</SelectItem>
              {(areas ?? []).map((a) => (
                <SelectItem key={a.key} value={a.key}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 cursor-pointer">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any status</SelectItem>
              <SelectItem value="PUBLISHED">Published</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
            {(
              [
                { id: "table", icon: List, label: "Table" },
                { id: "cards", icon: LayoutGrid, label: "Cards" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                title={opt.label}
                onClick={() => setView(opt.id)}
                className={
                  "flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors " +
                  (view === opt.id
                    ? "bg-emerald-600 text-white shadow"
                    : "text-muted-foreground hover:text-foreground")
                }
              >
                <opt.icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            ))}
          </div>

          {hasFilters && (
            <Button
              variant="ghost"
              className="cursor-pointer gap-1"
              onClick={() => {
                setAreaKey(ALL);
                setStatus(ALL);
                setSearch("");
              }}
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>

        {/* Lesson grid. Cards rather than a table: a lesson has a name, a
            place, a size and a state, and four of those in a row of table
            cells reads as a spreadsheet of something that is actually a
            piece of writing. */}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading content…
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed py-20 text-center text-muted-foreground">
            <BookOpen className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No lessons match these filters.
          </div>
        ) : view === "table" ? (
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Lesson</TableHead>
                  <TableHead>Area / Stage</TableHead>
                  <TableHead>Facet</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead className="text-right">Steps</TableHead>
                  <TableHead className="text-right">Questions</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((c) => (
                  <TableRow
                    key={c.slug}
                    onClick={() => setOpenSlug(c.slug)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <code className="text-[11px] text-muted-foreground">
                        {c.slug}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.areaName}
                      <span className="text-muted-foreground">
                        {" · "}
                        {c.stageName}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{c.facet}</TableCell>
                    <TableCell className="text-sm">
                      {c.difficulty ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.assetCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.questionCount}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={c.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {paged.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setOpenSlug(c.slug)}
                className="group cursor-pointer rounded-xl border bg-card p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-500/50 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold group-hover:text-emerald-600">
                      {c.name}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">
                      {c.areaName} · {c.stageName}
                    </p>
                  </div>
                  <StatusPill status={c.status} />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <Badge variant="outline" className="font-normal">
                    {c.facet}
                  </Badge>
                  {c.difficulty && (
                    <Badge variant="outline" className="font-normal">
                      {c.difficulty}
                    </Badge>
                  )}
                  <span className="ml-auto text-muted-foreground">
                    {c.assetCount} steps · {c.questionCount} questions
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {!isLoading && visible.length > 0 && (
          <div className="rounded-xl border bg-card p-3 shadow-sm">
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={visible.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      <LessonDialog slug={openSlug} onClose={() => setOpenSlug(null)} />

      <NewLessonDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(slug) => setOpenSlug(slug)}
      />
    </Layout>
  );
}

// ─── Lesson detail + editing ────────────────────────────────────────────

function LessonDialog({
  slug,
  onClose,
}: {
  slug: string | null;
  onClose: () => void;
}) {
  const { data: detail, isLoading } = useAcademyConcept(slug ?? undefined);
  const setStatus = useSetConceptStatus();
  const [adding, setAdding] = useState(false);
  const reorder = useReorderAssets();
  // Which row is being dragged, and which gap it is currently over.
  // Held here rather than in the rows because a row cannot know what
  // it is being dropped onto.
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [tab, setTab] = useState<"edit" | "preview">("edit");

  // Back to Edit whenever a different lesson opens — staying on
  // Preview would show the new lesson in a mode the author did not
  // choose for it.
  useEffect(() => {
    setTab("edit");
    setAdding(false);
  }, [slug]);

  const changeStatus = (next: ContentStatus) => {
    if (!slug) return;
    setStatus.mutate(
      { slug, status: next },
      {
        onSuccess: () => toast.success(`Lesson set to ${next.toLowerCase()}`),
        // The server's message is the useful part — it carries the
        // validator's own sentence, e.g. which zone id was mistyped.
        onError: (e: unknown) =>
          toast.error(extractError(e, "Could not change status")),
      }
    );
  };

  return (
    <Dialog open={!!slug} onOpenChange={(open) => !open && onClose()}>
      {/* sm:max-w-* rather than max-w-*: the base DialogContent carries
          sm:max-w-lg, and a breakpoint-prefixed utility wins over an
          unprefixed one — so plain max-w-4xl was silently capped at
          512px, which is what pushed the buttons off the edge.
          overflow-x-hidden because a dialog that scrolls sideways hides
          its own controls. */}
      <DialogContent className="max-h-[90vh] w-[min(96vw,1100px)] min-w-0 max-w-[96vw] overflow-y-auto overflow-x-hidden sm:max-w-[1100px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-8">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600/10 text-emerald-600">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-lg">
                {detail?.name ?? "Lesson"}
              </span>
              {detail && (
                <span className="block text-xs font-normal text-muted-foreground">
                  {detail.areaName} · {detail.stageName}
                </span>
              )}
            </span>
            {detail && <StatusPill status={detail.status} />}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        )}

        {detail && (
          <div className="min-w-0 space-y-5">
            <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
              {(
                [
                  { id: "edit", label: "Edit", icon: Pencil },
                  { id: "preview", label: "Preview", icon: Play },
                ] as const
              ).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setTab(o.id)}
                  className={
                    "flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all " +
                    (tab === o.id
                      ? "bg-emerald-600 text-white shadow"
                      : "text-muted-foreground hover:bg-background/60 hover:text-foreground")
                  }
                >
                  <o.icon className="h-4 w-4" />
                  {o.label}
                </button>
              ))}
            </div>
            {tab === "preview" ? (
              <LessonPreview concept={detail} />
            ) : (
            <div className="min-w-0 space-y-5">
            <p className="break-words rounded-xl border-l-4 border-emerald-500/40 bg-muted/40 p-3 text-sm leading-relaxed">
              {detail.definition}
            </p>

            <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-muted/20 p-2.5">
              <Badge variant="outline">{detail.areaName}</Badge>
              <Badge variant="outline">{detail.stageName}</Badge>
              <Badge variant="outline">{detail.facet}</Badge>
              {detail.difficulty && (
                <Badge variant="outline">{detail.difficulty}</Badge>
              )}
              <code className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {detail.slug}
              </code>

              <div className="ml-auto flex flex-wrap gap-2">
                {detail.status !== "PUBLISHED" && (
                  <Button
                    size="sm"
                    className="cursor-pointer gap-1.5"
                    disabled={setStatus.isPending}
                    onClick={() => changeStatus("PUBLISHED")}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Publish
                  </Button>
                )}
                {detail.status === "PUBLISHED" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="cursor-pointer gap-1.5"
                    disabled={setStatus.isPending}
                    onClick={() => changeStatus("DRAFT")}
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                    Unpublish
                  </Button>
                )}
                {detail.status !== "ARCHIVED" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="cursor-pointer gap-1.5 text-muted-foreground"
                    disabled={setStatus.isPending}
                    onClick={() => changeStatus("ARCHIVED")}
                  >
                    <Archive className="h-3.5 w-3.5" />
                    Archive
                  </Button>
                )}
              </div>
            </div>

            <LessonPlacement detail={detail} />

            <section>
              <SectionLabel>Learning goals</SectionLabel>
              <ul className="space-y-1.5">
                {detail.learningObjectives.map((o, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    {o}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <SectionLabel>
                  Lesson flow · {detail.assets.length} steps
                </SectionLabel>
                <Button
                  size="sm"
                  variant="outline"
                  className="cursor-pointer gap-1.5"
                  onClick={() => setAdding((v) => !v)}
                >
                  {adding ? (
                    <>
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5" />
                      Add step
                    </>
                  )}
                </Button>
              </div>

              {adding && slug && (
                <AddStepForm
                  slug={slug}
                  nextOrder={detail.assets.length}
                  onDone={() => setAdding(false)}
                />
              )}

              {detail.assets.length === 0 && !adding && (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="flex w-full cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed py-8 text-center hover:border-emerald-500/60"
                >
                  <Plus className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Add the first step
                  </span>
                  <span className="text-xs text-muted-foreground">
                    A lesson usually opens with Notice, then Reveal, then
                    its questions.
                  </span>
                </button>
              )}

              <div className="space-y-2">
                {detail.assets.map((a, i) => (
                  <div
                    key={a.id}
                    onDragOver={(e) => {
                      // Without preventDefault the browser refuses the
                      // drop and the whole gesture silently does nothing.
                      e.preventDefault();
                      if (dragFrom !== null && dragOver !== i) setDragOver(i);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragFrom;
                      setDragFrom(null);
                      setDragOver(null);
                      if (from === null || from === i || !slug) return;
                      const next = detail.assets.map((x) => x.id);
                      const [moved] = next.splice(from, 1);
                      next.splice(i, 0, moved);
                      reorder.mutate(
                        { slug, assetIds: next },
                        {
                          onSuccess: () => toast.success("Steps reordered"),
                          onError: (e2: unknown) =>
                            toast.error(extractError(e2, "Could not reorder")),
                        }
                      );
                    }}
                    className={
                      "rounded-xl transition-all " +
                      (dragOver === i && dragFrom !== null && dragFrom !== i
                        ? "ring-2 ring-emerald-500 ring-offset-2"
                        : "") +
                      (dragFrom === i ? " opacity-40" : "")
                    }
                  >
                    <AssetRow
                      asset={a}
                      onDragStart={() => setDragFrom(i)}
                      onDragEnd={() => {
                        setDragFrom(null);
                        setDragOver(null);
                      }}
                      busy={reorder.isPending}
                    />
                  </div>
                ))}
              </div>
            </section>
            </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Where a lesson sits, and what it waits for.
//
// Both of these had working APIs and no buttons, which meant a
// lesson filed into the wrong stage stayed there, and a prerequisite
// chain could only be read, never changed.
function LessonPlacement({ detail }: { detail: AcademyAdminConcept }) {
  const { data: areas } = useAcademyAreas();
  const { data: all } = useAcademyConcepts();
  const move = useMoveConcept();
  const setPrereqs = useSetPrerequisites();

  const [areaKey, setAreaKey] = useState(detail.areaKey);
  const [stageKey, setStageKey] = useState(detail.stageKey);

  useEffect(() => {
    setAreaKey(detail.areaKey);
    setStageKey(detail.stageKey);
  }, [detail.areaKey, detail.stageKey]);

  const stages = areas?.find((a) => a.key === areaKey)?.stages ?? [];
  const dirty =
    areaKey !== detail.areaKey || stageKey !== detail.stageKey;

  const chosen = new Set<string>(detail.prerequisites.map((pr) => pr.slug));

  const togglePrereq = (slug: string) => {
    const next: string[] = chosen.has(slug)
      ? [...chosen].filter((s) => s !== slug)
      : [...chosen, slug];
    setPrereqs.mutate(
      { slug: detail.slug, prerequisiteSlugs: next },
      {
        onSuccess: () => toast.success("Prerequisites updated"),
        // The server refuses a loop, and its sentence explains why —
        // every lesson in a cycle would be permanently locked.
        onError: (e: unknown) =>
          toast.error(extractError(e, "Could not update prerequisites")),
      }
    );
  };

  return (
    <section className="grid gap-4 md:grid-cols-2">
      <div>
        <SectionLabel>Where it sits</SectionLabel>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={areaKey}
            onValueChange={(v) => {
              setAreaKey(v);
              // Stages belong to an area, so one chosen under the
              // previous area means nothing now.
              setStageKey("");
            }}
          >
            <SelectTrigger className="w-40 cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(areas ?? []).map((a) => (
                <SelectItem key={a.key} value={a.key}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stageKey} onValueChange={setStageKey}>
            <SelectTrigger className="w-44 cursor-pointer">
              <SelectValue placeholder="Choose a stage" />
            </SelectTrigger>
            <SelectContent>
              {stages.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dirty && !!stageKey && (
            <Button
              size="sm"
              className="cursor-pointer"
              disabled={move.isPending}
              onClick={() =>
                move.mutate(
                  { slug: detail.slug, areaKey, stageKey },
                  {
                      onSuccess: () => toast.success("Lesson moved"),
                      onError: (e: unknown) =>
                        toast.error(extractError(e, "Could not move")),
                  }
                )
              }
            >
              Move
            </Button>
          )}
        </div>
      </div>

      <div>
        <SectionLabel>Requires first</SectionLabel>
        <div className="max-h-40 overflow-y-auto rounded-lg border p-2">
          <div className="flex flex-wrap gap-1">
            {(all ?? [])
              .filter((c) => c.slug !== detail.slug)
              .map((c) => {
                const on = chosen.has(c.slug);
                return (
                  <button
                    key={c.slug}
                    type="button"
                    disabled={setPrereqs.isPending}
                    onClick={() => togglePrereq(c.slug)}
                    className={
                      "cursor-pointer rounded border px-1.5 py-0.5 text-[10px] transition-colors " +
                      (on
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                        : "text-muted-foreground hover:border-emerald-500/50")
                    }
                  >
                    {c.name}
                  </button>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      <span className="h-3 w-1 rounded-full bg-emerald-500" />
      {children}
    </h3>
  );
}

function AssetRow({
  asset,
  onDragStart,
  onDragEnd,
  busy,
}: {
  asset: AcademyAdminAsset;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  busy?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const save = useUpdateAssetPayload();
  const remove = useDeleteAsset();
  // Form by default where one exists. The author who opens a zone
  // question wants to point at a zone, not to be shown an array of
  // strings and asked to be careful with the commas.
  const [mode, setMode] = useState<"form" | "json">(
    FORM_KINDS.has(asset.kind) ? "form" : "json"
  );

  // Confirmed, because a step is real writing and the server only
  // refuses this when the step already has recorded answers.
  const onDelete = () => {
    if (!window.confirm(`Delete step ${asset.order} (${asset.kindLabel})?`))
      return;
    remove.mutate(asset.id, {
      onSuccess: () => toast.success("Step deleted"),
      onError: (e: unknown) =>
        toast.error(extractError(e, "Could not delete the step")),
    });
  };

  // Re-seeded whenever the server's copy changes, so a save elsewhere in
  // the dialog cannot leave this textarea showing a stale payload.
  useEffect(() => {
    setDraft(JSON.stringify(asset.payload, null, 2));
  }, [asset.payload]);

  const onSave = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      // Caught here rather than sent, so a missing comma reads as a
      // missing comma instead of a 400 from the server.
      setJsonError(e instanceof Error ? e.message : "Invalid JSON");
      return;
    }
    setJsonError(null);
    save.mutate(
      { assetId: asset.id, payload: parsed },
      {
        onSuccess: () => {
          toast.success("Payload saved");
          setEditing(false);
        },
        onError: (e: unknown) =>
          setJsonError(extractError(e, "Could not save payload")),
      }
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm transition-colors hover:border-emerald-500/40">
      <div className="flex min-w-0 items-center gap-2 p-3">
        {/* Only the handle is draggable, not the whole row. A row that
            drags anywhere cannot also be clicked open, and the click is
            the thing done far more often. */}
        <span
          draggable={!busy}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          title="Drag to reorder"
          className="shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left text-sm"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-bold tabular-nums text-muted-foreground">
            {asset.order}
          </span>
          <span className="truncate font-semibold">{asset.kindLabel}</span>
          {asset.isGradeable && (
            <Badge variant="outline" className="shrink-0 gap-1 text-[10px]">
              <HelpCircle className="h-3 w-3" />
              question
            </Badge>
          )}
          <code className="ml-auto shrink-0 truncate text-[11px] text-muted-foreground">
            {asset.kind}
          </code>
        </button>

        <Button
          size="sm"
          variant="ghost"
          className="h-7 cursor-pointer gap-1 px-2 text-xs text-red-600 hover:text-red-700"
          disabled={remove.isPending}
          onClick={onDelete}
        >
          <Trash2 className="h-3 w-3" />
        </Button>

        <Button
          size="sm"
          variant="ghost"
          className="h-7 cursor-pointer gap-1 px-2 text-xs"
          onClick={() => {
            setOpen(true);
            setEditing((v) => !v);
          }}
        >
          {editing ? (
            <>
              <X className="h-3 w-3" />
              Cancel
            </>
          ) : (
            <>
              <Pencil className="h-3 w-3" />
              Edit
            </>
          )}
        </Button>
      </div>

      {open && (
        <div className="min-w-0 border-t bg-background p-3">
          {editing ? (
            <>
              {FORM_KINDS.has(asset.kind) && (
                <div className="mb-3 flex items-center gap-1 rounded-xl bg-muted p-1">
                  {(["form", "json"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className={
                        "flex-1 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold transition-all " +
                        (mode === m
                          ? "bg-emerald-600 text-white shadow"
                          : "text-muted-foreground hover:text-foreground")
                      }
                    >
                      {m === "form" ? "Form" : "Raw JSON"}
                    </button>
                  ))}
                </div>
              )}

              {FORM_KINDS.has(asset.kind) && mode === "form" ? (
                <PayloadForm
                  kind={asset.kind}
                  payload={safeParse(draft) ?? {}}
                  onChange={(next) => setDraft(JSON.stringify(next, null, 2))}
                />
              ) : (
              <>
              {/* A raw JSON editor, not a form per question type. Ten
                  question types would mean ten forms to keep in step with
                  the schema, and the validator already rejects anything
                  wrong — so the honest tool is the payload itself plus
                  the real error message. */}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                className="h-72 w-full resize-y rounded border bg-muted/40 p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-emerald-500/40"
              />
              </>
              )}
              {jsonError && (
                <div className="mt-2 flex gap-2 rounded border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-600">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="whitespace-pre-wrap">{jsonError}</span>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Button
                  size="sm"
                  className="cursor-pointer gap-1.5"
                  disabled={save.isPending}
                  onClick={onSave}
                >
                  {save.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  Save payload
                </Button>
                <span className="text-[11px] text-muted-foreground">
                  Checked against the same rules the seed uses.
                </span>
              </div>
            </>
          ) : (
            // Reading, not editing. The payload in words on the left,
            // the learner's screen on the right — between them they
            // answer both questions an author opens a step to ask:
            // what does it say, and how will it land. Raw JSON is
            // still here, one click down, for when it is the payload
            // itself you need to see.
            <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
              <div className="min-w-0 space-y-2">
                <PayloadSummary
                  payload={asset.payload as Record<string, unknown>}
                />
                <details className="min-w-0 rounded-lg border bg-muted/30">
                  <summary className="cursor-pointer px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Raw JSON
                  </summary>
                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words border-t p-3 font-mono text-[11px]">
                    {JSON.stringify(asset.payload, null, 2)}
                  </pre>
                </details>
              </div>

              <div className="min-w-0">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  On a phone
                  {asset.isGradeable && (
                    <span className="ml-1 font-normal normal-case">
                      · answer shown revealed
                    </span>
                  )}
                </p>
                <div
                  className="overflow-hidden rounded-xl border p-3"
                  style={{
                    background: PITCH_COLOR.boardPitchBg,
                    color: PITCH_COLOR.ink,
                  }}
                >
                  <AssetPreview
                    kind={asset.kind}
                    payload={asset.payload as Record<string, unknown>}
                    width={300}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function AddStepForm({
  slug,
  nextOrder,
  onDone,
}: {
  slug: string;
  nextOrder: number;
  onDone: () => void;
}) {
  const { data: kinds } = useAcademyAssetKinds();
  const { data: vocab } = useAcademyVocabulary();
  const create = useCreateAsset();
  const [kind, setKind] = useState("");
  const [payload, setPayload] = useState("{}");
  const [mode, setMode] = useState<"form" | "json">("form");
  const [showRef, setShowRef] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = kinds?.find((k) => k.key === kind);
  const hasForm = FORM_KINDS.has(kind);

  // Split the picker in two. "What kind of step?" over eighteen flat
  // options asks an author to already know which of them are questions —
  // and the difference between a step that teaches and a step that costs
  // a life is the most important thing about the choice.
  const teaching = (kinds ?? []).filter((k) => !k.isGradeable);
  const questions = (kinds ?? []).filter((k) => k.isGradeable);

  const pickKind = (k: string) => {
    setKind(k);
    setMode(FORM_KINDS.has(k) ? "form" : "json");
    // Start from the kind's worked example. Only over an untouched
    // editor: silently replacing something already typed would be worse
    // than making someone clear it themselves.
    const next = kinds?.find((x) => x.key === k)?.template;
    if (next && (payload.trim() === "{}" || payload.trim() === "")) {
      setPayload(JSON.stringify(next, null, 2));
    }
  };

  const submit = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid JSON");
      return;
    }
    setError(null);
    create.mutate(
      {
        slug,
        data: {
          kind,
          payload: parsed,
          // Only gradeable steps carry a weight; sending one for a
          // reading step would be meaningless data on the row.
          masteryWeight: selected?.isGradeable ? 0.8 : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Step added");
          setPayload("{}");
          setKind("");
          onDone();
        },
        onError: (e: unknown) =>
          setError(extractError(e, "Could not add the step")),
      }
    );
  };

  return (
    <div className="mb-3 min-w-0 space-y-3 rounded-xl border-2 border-dashed border-emerald-500/40 bg-emerald-500/[0.04] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={kind} onValueChange={pickKind}>
          <SelectTrigger className="w-80 cursor-pointer">
            <SelectValue placeholder="What kind of step?" />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Teaching
            </div>
            {teaching.map((k) => (
              <SelectItem key={k.key} value={k.key}>
                {k.label}
              </SelectItem>
            ))}
            <div className="mt-1 border-t px-2 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Questions — these cost a life
            </div>
            {questions.map((k) => (
              <SelectItem key={k.key} value={k.key}>
                {k.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          Goes in at position {nextOrder}
        </span>
      </div>

      {(selected?.hint || selected?.description) && (
        <p className="text-xs text-muted-foreground">
          {selected.hint ?? selected.description}
        </p>
      )}

      {!kind && (
        <p className="text-xs text-muted-foreground">
          Pick a kind and its fields appear here, already filled with a
          working example.
        </p>
      )}

      {!!kind && hasForm && (
        <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
          {(["form", "json"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "flex-1 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold transition-all " +
                (mode === m
                  ? "bg-emerald-600 text-white shadow"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              {m === "form" ? "Form" : "Raw JSON"}
            </button>
          ))}
        </div>
      )}

      {!!kind && hasForm && mode === "form" ? (
        <div className="rounded-lg border bg-background p-3">
          <PayloadForm
            kind={kind}
            payload={safeParse(payload) ?? {}}
            onChange={(next) => setPayload(JSON.stringify(next, null, 2))}
          />
        </div>
      ) : !!kind ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {selected?.template && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 cursor-pointer gap-1 text-xs"
                onClick={() =>
                  setPayload(JSON.stringify(selected.template, null, 2))
                }
              >
                Reset to example
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 cursor-pointer gap-1 text-xs"
              onClick={() => setShowRef((v) => !v)}
            >
              {showRef ? "Hide" : "Show"} allowed ids
            </Button>
          </div>

          {/* Served by the API rather than typed here, so adding a zone
              to the app cannot leave this list quietly wrong. */}
          {showRef && vocab && (
            <div className="min-w-0 space-y-2 rounded border bg-background p-3 text-[11px]">
              <div className="break-words">
                <span className="font-semibold">Zones: </span>
                <code className="text-muted-foreground">
                  {vocab.zoneIds.join(", ")}
                </code>
              </div>
              <div className="break-words">
                <span className="font-semibold">Markings: </span>
                <code className="text-muted-foreground">
                  {vocab.markingIds.join(", ")}
                </code>
              </div>
              {vocab.formations.map((f) => (
                <div key={f.key} className="break-words">
                  <span className="font-semibold">{f.key}: </span>
                  <code className="text-muted-foreground">
                    {f.roles.join(", ")}
                  </code>
                </div>
              ))}
              <div className="break-words">
                <span className="font-semibold">Beat visuals: </span>
                <code className="text-muted-foreground">
                  {vocab.beatVisualKinds.map((b) => b.kind).join(", ")}
                </code>
              </div>
            </div>
          )}

          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            spellCheck={false}
            className="h-56 w-full resize-y rounded border bg-background p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-emerald-500/40"
          />
        </>
      ) : null}

      {error && (
        <div className="flex gap-2 rounded border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-600">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
        </div>
      )}

      <Button
        size="sm"
        className="cursor-pointer gap-1.5"
        disabled={!kind || create.isPending}
        onClick={submit}
      >
        {create.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Plus className="h-3.5 w-3.5" />
        )}
        Add step
      </Button>
    </div>
  );
}

// The form edits an object while the textarea edits text, and the two
// share one draft. A half-typed JSON string must not blank the form, so
// a failed parse returns null and the caller falls back to an empty
// object rather than throwing mid-render.
function safeParse(s: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(s);
    return v && typeof v === "object" && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

// The server's message is the useful half of an error here — it carries
// the validator's own sentence, e.g. which zone id was mistyped — so it
// is preferred over the generic axios one whenever it exists.
function extractError(e: unknown, fallback: string): string {
  const res = (e as { response?: { data?: { message?: unknown } } })?.response;
  const msg = res?.data?.message;
  if (Array.isArray(msg)) return msg.join("\n");
  if (typeof msg === "string") return msg;
  return e instanceof Error ? e.message : fallback;
}
